import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { WithLabel } from '@/module/auth/sign-up';
import { LoadingSpinner } from '@/shared/components';
import { useCountdownTimer } from '@/shared/hooks';
import { Input } from '@/ui';
import { Button } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { isCodeSentAtom, isEditingAtom, isVerifiedAtom, phoneNumberAtom, timerResetKeyAtom } from '../atoms/phoneVerificationAtoms';
import { useSendSmsCode } from '../hooks';
import { DEFAULT_COUNTRY_CODE, toE164Format } from '../utils/countryCodeData';
import { CountryCodeSelector } from './CountryCodeSelector';
const CODE_EXPIRY_SECONDS = 60 * 5;
interface PhoneNumberInputProps {
  verifiedPhoneNumber?: string;
  onSendSuccess?: () => void;
}
export const PhoneNumberInput = ({
  verifiedPhoneNumber,
  onSendSuccess
}: PhoneNumberInputProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isVerificationPage = router.pathname.includes('/verification-phone');
  const [isEditing, setIsEditing] = useAtom(isEditingAtom);
  const [isCodeSent, setIsCodeSent] = useAtom(isCodeSentAtom);
  const isVerified = useAtomValue(isVerifiedAtom);
  const setPhoneNumber = useSetAtom(phoneNumberAtom);
  const location = isVerificationPage ? 'phone_verification' : 'profile_edit';
  const from = router.query.from as 'signup' | 'profile_edit' | 'banner_modal' || undefined;
  const trackEvent = (eventName: string) => {
    logger.track(eventName, {
      location,
      from
    });
  };

  // 인증 페이지에서는 항상 편집 모드로 시작
  useEffect(() => {
    if (isVerificationPage) {
      setIsEditing(true);
    }
  }, [isVerificationPage, setIsEditing]);
  const {
    register,
    watch,
    reset: resetForm,
    formState: {
      isDirty
    }
  } = useForm<{
    phoneNumber: string;
  }>({
    defaultValues: {
      phoneNumber: verifiedPhoneNumber?.slice(3) || ''
    }
  });
  const phoneNumber = watch('phoneNumber');
  const {
    sendCode,
    isLoading: isSending,
    error: sendError,
    reset: resetSend
  } = useSendSmsCode({
    onSuccess: () => {
      onSendSuccess?.();
      // E.164 형식으로 변환하여 atom에 저장
      const formattedPhoneNumber = toE164Format(phoneNumber, DEFAULT_COUNTRY_CODE);
      setPhoneNumber(formattedPhoneNumber);
      setIsCodeSent(true);
      // 현재 값을 기준값으로 설정하여 isDirty를 false로 초기화
      resetForm({
        phoneNumber
      });
    },
    onError: error => {
      logger.captureError(error, {
        tags: {
          errorType: 'send_sms_code_error'
        },
        extra: {
          location
        }
      });
    }
  });

  // 번호 변경 시 모든 상태 초기화
  const handleResetVerification = () => {
    resetSend();
    setPhoneNumber('');
    setIsCodeSent(false);
  };
  return <WithLabel label={t('phoneNumberInput.title')} errorMessage={sendError ? t(sendError) : undefined} data-sentry-element="WithLabel" data-sentry-component="PhoneNumberInput" data-sentry-source-file="PhoneNumberInput.tsx">
      <div className="flex flex-col items-center gap-xs mobile-lg:flex-row">
        <Input key={isEditing ? 'editing' : 'disabled'} className={cn('border-input-border h-[48px] w-full rounded-sm focus-within:border-strong focus-within:border-[2px]', isVerified && 'pointer-events-none')} placeholder={t('phoneNumberInput.placeholder')} {...register('phoneNumber', {
        onChange: e => {
          e.target.value = e.target.value.replace(/[^0-9]/g, '');
        }
      })} leftSlot={<div className="flex shrink-0 items-center">
              <CountryCodeSelector className="h-[46px] shrink-0" />
              <span className="shrink-0 pr-xs text-body-lg-medium text-default">
                +82
              </span>
            </div>} rightSlot={!isVerified && <RightSlot isDirty={isDirty} onButtonClick={handleResetVerification} />} readOnly={isVerified} disabled={!isEditing} autoFocus={isEditing} data-sentry-element="Input" data-sentry-source-file="PhoneNumberInput.tsx" />
        {!isCodeSent && <Button variant={isEditing ? 'contained' : 'outlined'} intent={isEditing ? 'strong' : 'neutral'} size="lg" className="w-full min-w-[120px] shrink-0 mobile-lg:w-auto" leftSlot={isSending ? <LoadingSpinner className="size-4 text-white" /> : undefined} onClick={() => {
        if (!isEditing) {
          trackEvent('phone_number_change_click');
          setIsEditing(true);
          return;
        }
        trackEvent('phone_auth_request_click');
        sendCode(phoneNumber);
      }}>
            {isEditing ? t('phoneNumberInput.sendCode') : t('phoneNumberInput.changeNumber')}
          </Button>}
      </div>
    </WithLabel>;
};

// 번호 변경 버튼과 타이머를 표시하는 컴포넌트
const RightSlot = ({
  isDirty,
  onButtonClick
}: {
  isDirty: boolean;
  onButtonClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const isCodeSent = useAtomValue(isCodeSentAtom);
  const timerResetKey = useAtomValue(timerResetKeyAtom);
  const {
    formattedTime,
    isExpired
  } = useCountdownTimer({
    initialSeconds: CODE_EXPIRY_SECONDS,
    enabled: isCodeSent,
    resetKey: timerResetKey
  });
  return <div className="flex items-center gap-xs" data-sentry-component="RightSlot" data-sentry-source-file="PhoneNumberInput.tsx">
      {isCodeSent && isDirty && <Button type="button" variant="transparent" intent="neutral" size="xs" onClick={onButtonClick} className="shrink-0">
          {t('phoneNumberInput.change')}
        </Button>}

      {isCodeSent && <span className={cn('shrink-0 text-body-lg-medium basis-[44px]', isExpired ? 'text-destructive' : 'text-default')}>
          {formattedTime}
        </span>}
    </div>;
};